import {
  FocusGroupsService,
  type FocusGroupResponseDto,
} from '@inference.engine/persona-api-client';
import { useQuery } from '@tanstack/react-query';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { personasHttp } from '@/lib/api/personas-client';

type GetFocusGroupByIdOptions = {
  suppressErrorNotification?: boolean;
};

const getFocusGroupById = async (
  id: string,
  options?: GetFocusGroupByIdOptions,
): Promise<FocusGroupResponseDto> => {
  const response = await FocusGroupsService.getById({
    client: personasHttp,
    path: { id },
    // When the resolution seam is active it owns the not-found UX, so a
    // cross-workspace 404 must not flash the global toast. Opt-in only:
    // every other consumer keeps the pre-seam toast behavior.
    headers: options?.suppressErrorNotification
      ? // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
        { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' }
      : undefined,
  });

  if (!response.data) {
    throw new Error('Focus group not found');
  }

  return response.data;
};

type UseFocusGroupOptions = {
  suppressErrorNotification?: boolean;
  /** Extra gate on top of the id guard — lets the deep-link seam hold the fetch until resolution settles. */
  enabled?: boolean;
};

export const useFocusGroup = (focusGroupId: string | undefined, options?: UseFocusGroupOptions) => {
  return useQuery({
    // `options` only tunes toast suppression / the enabled gate — the fetched
    // data is identical, so it must NOT split the cache bucket.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['focusGroup', focusGroupId],
    queryFn: () =>
      getFocusGroupById(focusGroupId!, {
        suppressErrorNotification: options?.suppressErrorNotification,
      }),
    enabled: !!focusGroupId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  });
};
